import React, { useCallback, useEffect } from 'react';
import { useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import useUserDetails from 'utils/hooks/auth/useUserDetails';
import { toggleFileUploader } from 'store/entities/submissionDetail';
import { uploadFileDetailView } from 'store/entities/submissionDetail/thunks';
import { getSubmission } from 'store/entities/submissionDetail/thunks';
import { formatFileSize, resolveFileTypeAcceptedExtensions } from 'utils/fileUtils';
import { notifyError } from 'utils/notifications';
import { formatDateTime } from 'utils/dateUtils';

import Button from 'components/UIComponents/Button';
import { Column, Row, Section } from 'components/UIComponents/Layout';
import Modal, { ModalCard } from 'components/UIComponents/Modal';
import LoadingSpinner from 'components/UIComponents/LoadingSpinner';

export default () => {
  const { submissionId } = useParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { userId } = useUserDetails();

  const { fileUploaderOpen, fileUploading, submissionDetail } = useSelector(
    state => state.submissionDetail
  );

  const { obligation = {}, dueDate } = submissionDetail;
  const { filetype, tableDto, reporter, institutionDto } = obligation;
  const tableName = tableDto?.obligation;
  const accept = resolveFileTypeAcceptedExtensions(filetype);

  const {
    acceptedFiles,
    fileRejections,
    getRootProps,
    getInputProps,
  } = useDropzone({
    maxFiles: 1,
    disabled: fileUploading,
    accept,
  });

  const resetUploader = useCallback(() => {
    acceptedFiles.splice(0, 1);
    fileRejections.splice(0, 1);
  }, [acceptedFiles, fileRejections]);

  const closeUploader = () => dispatch(toggleFileUploader(false));

  const prepareFile = () => {
    const file = acceptedFiles[0];
    const fileName = file.name.replace(/\.[^/.]+$/, '');
    const namesMatch = fileName === tableName;
    let payload = new FormData();
    payload.append('file', file);

    if (!namesMatch) {
      window.confirm(
        t('datagate.external.submission.fileUpload.nameCheck', {
          tableName,
          fileName,
          dueDate: formatDateTime(dueDate),
        })
      ) && upload(payload);
    } else upload(payload);
  };

  const upload = async payload => {
    const uploadResult = await dispatch(
      uploadFileDetailView({ submissionId, payload })
    );

    if (uploadFileDetailView.fulfilled.match(uploadResult)) {
      closeUploader();
      dispatch(getSubmission(submissionId));
    }
  };

  useEffect(() => {
    if (fileRejections.length > 0 && filetype)
      notifyError(
        t('datagate.external.submission.fileUpload.message.fileTypeError', {
          filetype: accept,
        })
      );
  }, [fileRejections, accept, filetype, t]);

  useEffect(() => {
    if (!fileUploaderOpen) {
      resetUploader();
    }
  }, [fileUploaderOpen, resetUploader]);

  const hasFile = acceptedFiles.length > 0;
  const userIsNotReporter = reporter?.id !== userId;
  const mainUser = institutionDto?.mainUser;

  return (
    <Modal
      show={fileUploaderOpen}
      onClose={closeUploader}
      closeOnBlur={!fileUploading}
    >
      <ModalCard>
        <LoadingSpinner
          loading={fileUploading}
          message={t(
            'datagate.external.submission.fileUpload.message.fileUploading'
          )}
        />
        <ModalCard.Head>
          <ModalCard.Title>
            {t('datagate.external.submission.fileUpload.title')}
          </ModalCard.Title>
        </ModalCard.Head>
        <ModalCard.Body>
          <Section className="vertical-padding-md">
            <Row>
              <Column>
                {t('datagate.external.submission.fileUpload.title')}{' '}
                {`${tableName}.${filetype}`}
              </Column>
            </Row>
            <Row>
              <Column>
                <div {...getRootProps({ className: 'dropzone' })}>
                  <input {...getInputProps()} />
                  <p>
                    {t(
                      'datagate.external.submission.fileUpload.message.uploadBox'
                    )}
                  </p>
                </div>
              </Column>
            </Row>
            {hasFile && (
              <Row>
                <Column>
                  <h4>
                    {t('datagate.external.submission.fileUpload.label.file')}
                  </h4>
                  <ul>
                    {acceptedFiles.map(file => (
                      <li key={file.path}>
                        {file.path} - {formatFileSize(file.size)}
                      </li>
                    ))}
                  </ul>
                </Column>
              </Row>
            )}
            {userIsNotReporter && (
              <Row>
                <Column>
                  {t(
                    'datagate.external.submission.fileUpload.message.userIsNotReporter',
                    {
                      tableName,
                      firstName: mainUser?.firstName,
                      lastName: mainUser?.lastName,
                    }
                  )}
                </Column>
              </Row>
            )}
          </Section>
        </ModalCard.Body>
        <ModalCard.Foot>
          <Button
            color="primary"
            type="button"
            disabled={fileUploading}
            onClick={closeUploader}
            outlined
            id="closeUploaderButton"
          >
            &#215; {t('datagate.external.submission.fileUpload.button.cancel')}
          </Button>
          {hasFile && (
            <Button
              type="button"
              color="primary"
              disabled={fileUploading}
              onClick={prepareFile}
              id="confirmUploaderButton"
            >
              {t('datagate.external.submission.fileUpload.button.upload')}
            </Button>
          )}
        </ModalCard.Foot>
      </ModalCard>
    </Modal>
  );
};
